'use client';

import marketAnimation from '@assets/lottie/nav/market.json';
import newsAnimation from '@assets/lottie/nav/news.json';
import searchAnimation from '@assets/lottie/nav/search.json';
import tradeAnimation from '@assets/lottie/nav/trade.json';
import walletAnimation from '@assets/lottie/nav/wallet.json';
import { useLottieHoverActivePlay } from '@/lib/hooks/useLottieHoverActivePlay';
import { LazyLottie as Lottie } from '@/lib/ui-kit/lazy-lottie';
import { cn } from '@/lib/ui-kit/utils';

export type BottomNavIconName = 'markets' | 'search' | 'wallet' | 'news' | 'trade';

const BOTTOM_NAV_ANIMATIONS = {
  markets: marketAnimation,
  search: searchAnimation,
  wallet: walletAnimation,
  news: newsAnimation,
  trade: tradeAnimation,
} as const satisfies Record<BottomNavIconName, unknown>;

interface BottomNavIconProps {
  name: BottomNavIconName;
  /** The item is the active route — plays the animation once on activation. */
  active: boolean;
  /** The item is hovered — plays the animation once on hover-enter. */
  hovered: boolean;
  size?: number;
  className?: string;
}

/**
 * Animated Lottie icon for the mobile/tablet bottom navigation. The Lottie is
 * monochrome and recoloured to `currentColor` via the `.bottom-nav-icon` CSS
 * rule, so it follows the active/hover text colour. Playback is driven by the
 * parent: plays once from the start on hover-enter and when the item becomes
 * active.
 */
export function BottomNavIcon({ name, active, hovered, size = 22, className }: BottomNavIconProps) {
  const lottieRef = useLottieHoverActivePlay({ hovered, active });

  return (
    <Lottie
      lottieRef={lottieRef}
      animationData={BOTTOM_NAV_ANIMATIONS[name]}
      loop={false}
      autoplay={false}
      className={cn('bottom-nav-icon', className)}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        flexShrink: 0,
        lineHeight: 0,
      }}
    />
  );
}
